import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components/macro';
import { useHistory, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  FullMessageTemplateFragment,
  FullMessageTemplateFragmentDoc,
  MessageTemplateCreateInput,
  useCreateMessageTemplateMutation,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { MessageTemplatesIcon } from 'assets/icons';
import { RouteBasePaths } from 'routes';
import MessageTemplateView from './MessageTemplateView';
import useArchiveMessageTemplate from './hooks/useArchiveMessageTemplate';
import useMessageTemplates from './hooks/useMessageTemplates';
import sortBy from 'lodash-es/sortBy';
import { truncateString } from 'shared/utils';
import Tooltip from 'shared/components/Tooltip';
import { Reference } from '@apollo/client';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Menu,
  useToast,
} from '@televet/kibble-ui';
import { useAuth } from 'shared/providers/AuthProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { MessageTemplateEvents } from 'shared/providers/TrackingProvider/EventNames';

const MessageTemplates = (): JSX.Element => {
  const { clinicUser, currentClinicId } = useClinicUser();
  const { isImpersonating } = useAuth();
  const history = useHistory();
  const { messageTemplateId } = useParams<{ messageTemplateId?: string }>();
  const { trackEvent } = useTracking();
  const toast = useToast();

  const { messageTemplates } = useMessageTemplates({ clinicId: currentClinicId || '', isImpersonating });

  // Navigate from `/templates` to the first template when data returns
  useEffect(() => {
    if (messageTemplates?.length && !messageTemplateId) {
      const firstUnarchivedMessageTemplate = messageTemplates.find(({ isArchived }) => !isArchived);
      if (firstUnarchivedMessageTemplate) {
        history.push(`${RouteBasePaths.MessageTemplates}/${firstUnarchivedMessageTemplate.id}`);
      }
    }
  }, [messageTemplateId, messageTemplates, history]);

  const [createMessageTemplate, { loading: isCreatingMessageTemplate }] = useCreateMessageTemplateMutation({
    update(cache, { data }) {
      cache.modify({
        fields: {
          findManyMessageTemplate(existingMessageTemplateRefs = []): Reference[] {
            if (!data?.createOneMessageTemplate) return existingMessageTemplateRefs;
            const newMessageTemplateRef = cache.writeFragment({
              data: data.createOneMessageTemplate,
              fragment: FullMessageTemplateFragmentDoc,
            });
            return [...existingMessageTemplateRefs, newMessageTemplateRef];
          },
        },
      });
    },
    onCompleted: (data) => {
      history.push(`${RouteBasePaths.MessageTemplates}/${data.createOneMessageTemplate.id}`);
    },
  });

  const handleAddTemplateClick = useCallback(
    async (data?: MessageTemplateCreateInput) => {
      if (!clinicUser?.id || !currentClinicId) return;
      try {
        await createMessageTemplate({
          variables: {
            data: data || {
              name: 'New Message Template',
              body: '',
              updatedBy: { connect: { id: clinicUser?.id } },
              clinic: { connect: { id: currentClinicId } },
            },
          },
        });
        trackEvent(MessageTemplateEvents.TemplateCreated);
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        toast({
          status: 'error',
          title: 'Whoops! An error occurred while adding a new message template. Please try again.',
        });
      }
    },
    [clinicUser?.id, currentClinicId, createMessageTemplate, trackEvent, toast],
  );

  useEffect(() => {
    if (messageTemplates?.length === 0 && !isCreatingMessageTemplate) {
      handleAddTemplateClick({
        name: 'Your First Message Template',
        body: 'Create a commonly-used response to send to your clients and team members in Conversations.',
        updatedBy: { connect: { id: clinicUser?.id } },
        clinic: { connect: { id: currentClinicId } },
      });
    }
  }, [isCreatingMessageTemplate, messageTemplates, clinicUser, currentClinicId, handleAddTemplateClick]);

  const { archiveMessageTemplate } = useArchiveMessageTemplate();

  const unarchivedMessageTemplates = useMemo(
    () => messageTemplates?.filter(({ isArchived }) => !Boolean(isArchived)) || [],
    [messageTemplates],
  );
  const archivedMessageTemplates = useMemo(
    () => messageTemplates?.filter(({ isArchived }) => Boolean(isArchived)) || [],
    [messageTemplates],
  );
  const currentMessageTemplate = useMemo(
    () => messageTemplates?.find(({ id }) => id === messageTemplateId),
    [messageTemplateId, messageTemplates],
  );

  const renderList = (messageTemplates: FullMessageTemplateFragment[]): JSX.Element[] => {
    const sortedMessageTemplates = sortBy(messageTemplates, ({ name }) => name.toLowerCase());
    return sortedMessageTemplates.map((messageTemplate) => {
      const { id, name, updatedBy, isArchived } = messageTemplate;
      const userName = [updatedBy?.firstName, updatedBy?.lastName].join(' ').trim();
      return (
        <ListItemContainer
          key={id}
          isActive={id === messageTemplateId}
          onClick={(): void => {
            history.push(`${RouteBasePaths.MessageTemplates}/${id}`);
          }}
        >
          {/* TODO: Remove */}
          <StyledMessageTemplatesIcon />
          <ItemDescriptionContainer>
            <ItemHeaderContainer>
              <Tooltip content={name} disabled={name.length <= 53}>
                <TemplateName>{truncateString(name, 53)}</TemplateName>
              </Tooltip>
              <Menu
                listProps={{
                  menuItems: [
                    {
                      label: isArchived ? 'Unarchive Template' : 'Archive Template',
                      value: messageTemplate.id,
                      onSelect: (): void => archiveMessageTemplate(messageTemplate),
                    },
                  ],
                }}
                buttonProps={{
                  variant: 'ghostNeutral',
                  size: 'md',
                  showRightIcon: false,
                  leftIconName: 'dotsVertical',
                }}
              />
            </ItemHeaderContainer>
            <ModifiedContainer>
              <ModifiedBy>Modified by {userName || 'Unknown'}</ModifiedBy>
            </ModifiedContainer>
          </ItemDescriptionContainer>
        </ListItemContainer>
      );
    });
  };

  return (
    <MessageTemplatesContainer>
      <MessageTemplatesContent>
        <MessageTemplateList>
          <Header>
            <Button
              iconName="plus"
              isLoading={isCreatingMessageTemplate}
              disabled={isCreatingMessageTemplate}
              onClick={(): Promise<void> => handleAddTemplateClick()}
            >
              Add New
            </Button>
          </Header>
          <Accordion defaultIndex={[0]} allowMultiple pt={1} pb={3}>
            {!!unarchivedMessageTemplates.length && (
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <CategoryTitle>Active</CategoryTitle>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pl={0} pr={0} pb={0}>
                  {renderList(unarchivedMessageTemplates)}
                </AccordionPanel>
              </AccordionItem>
            )}
            {!!archivedMessageTemplates.length && (
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <CategoryTitle>Archived</CategoryTitle>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pl={0} pr={0} pb={0}>
                  {renderList(archivedMessageTemplates)}
                </AccordionPanel>
              </AccordionItem>
            )}
          </Accordion>
        </MessageTemplateList>
      </MessageTemplatesContent>
      <MessageTemplateView key={currentMessageTemplate?.id} messageTemplate={currentMessageTemplate} />
    </MessageTemplatesContainer>
  );
};

const MessageTemplatesContainer = styled.div`
  display: flex;
  height: 100%;
  max-width: 100%;
`;

const MessageTemplatesContent = styled.div`
  height: 100%;
  overflow-y: scroll;
`;

const MessageTemplateList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 380px;
  min-width: 380px;
  margin: 0 20px 0;
`;

const Header = styled.h1`
  flex: 0 0 auto;
  height: 70px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: var(--chakra-colors-text-default);
  font-size: 20px;
  font-weight: 600;
`;

interface IListItemContainerProps {
  isActive: boolean;
}

const ListItemContainer = styled.div<IListItemContainerProps>`
  display: flex;
  border-left: ${(props): string => (props.isActive ? '4px solid #5fbed0' : '4px solid transparent')};
  background-color: var(--chakra-colors-background-default);
  border-radius: 6px;
  box-shadow: 0 2px 6px 0 rgba(110, 115, 114, 0.13);
  box-sizing: border-box;
  cursor: pointer;
  height: 108px;
  margin-bottom: 20px;
  padding: 20px 10px 20px 20px;
  width: 100%;

  &:hover {
    border-left: ${({ isActive }): string => (isActive ? '4px solid #5fbed0' : '4px solid #ddd')};
  }
`;

const ItemDescriptionContainer = styled.div`
  flex: 1 1 auto;
  font-size: 14px;
  color: var(--chakra-colors-text-default);
  overflow: hidden;

  * {
    overflow: hidden;
    white-space: normal;
  }
`;

const ItemHeaderContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const TemplateName = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const ModifiedContainer = styled.div`
  display: flex;
  align-items: center;
`;
const ModifiedBy = styled.div`
  flex: 1 1 auto;
`;

const StyledMessageTemplatesIcon = styled(MessageTemplatesIcon)`
  flex: 0 0 auto;
  height: 26px;
  width: auto;
  margin-right: 15px;

  * {
    fill: #44496b;
  }
`;

const CategoryTitle = styled.p`
  color: var(--chakra-colors-text-default);
  font-weight: 600;
  padding: 5px 0;
`;

export default MessageTemplates;
